import {
  AbsoluteFill,
  Img,
  interpolate,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { resolveSrc } from "./mediaSrc.ts";
import { SpeakerCardStill } from "./SpeakerCardStill.tsx";
import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  LEGACY_SPEAKER_CARD_LEFT_PX,
  LEGACY_SPEAKER_CARD_TOP_PX,
  LEGACY_SPEAKER_CARD_WIDTH_PCT,
  SPEAKER_STILL_FOUNDING_WIDTH,
  SPEAKER_STILL_HEIGHT,
  SPEAKER_STILL_WIDTH,
  type SpeakerOverlay,
} from "./types.ts";

/** Free x/y placement of the card (% of the 2160×3840 canvas, top-left
 *  anchor). Omitted → the founding CARD_LEFT_PX / CARD_TOP_PX. */
export interface SpeakerCardPlacement {
  xPct: number;
  yPct: number;
  /** Size multiplier over the founding 35%-width card. Absent → 1. */
  scale?: number;
  /** Card timing + still typography travel with the layout block but are
   *  consumed UPSTREAM (buildSpeakerOverlays / ensureSpeakerPng) — placement
   *  accepts and ignores them so the same object can be threaded verbatim. */
  durationSec?: number;
  nameFontPx?: number;
  descriptionWords?: number;
}

interface Props {
  speakers: SpeakerOverlay[];
  /** Per-channel card placement. Omitted → founding coords (byte-identical). */
  card?: SpeakerCardPlacement;
}

const FADE_FRAMES = 18;
/** Card placement (matches CapCut Scale 35%). Single-sourced in types.ts
 *  (LEGACY_SPEAKER_CARD_*) and shared with CoverFrame + the Studio edit
 *  layer so the cover's full-opacity card and the Studio baseline sit
 *  EXACTLY where the in-video card appears — one source of truth. */
export const CARD_TOP_PX = LEGACY_SPEAKER_CARD_TOP_PX;
export const CARD_LEFT_PX = LEGACY_SPEAKER_CARD_LEFT_PX;
export const CARD_WIDTH = `${LEGACY_SPEAKER_CARD_WIDTH_PCT}%`;

/** Resolve a card placement (% of canvas) to top-left px, rounding so the
 *  founding percentages (130/2160, 320/3840) reproduce 130 / 320 EXACTLY
 *  (raw float gives 319.9999…). Omitted → the founding px constants. */
export function resolveCardPx(card?: SpeakerCardPlacement): { leftPx: number; topPx: number } {
  if (!card) return { leftPx: CARD_LEFT_PX, topPx: CARD_TOP_PX };
  return {
    leftPx: Math.round((card.xPct / 100) * CANVAS_WIDTH),
    topPx: Math.round((card.yPct / 100) * CANVAS_HEIGHT),
  };
}

/** Card width CSS: the founding "35%" string EXACTLY for an unscaled founding
 *  1080px PNG (byte-identical), else 35% × scale × pngWidthPx/1080. Scaling by
 *  the PNG's intrinsic width keeps the still→canvas glyph scale at the
 *  founding 0.7 no matter which canvas generated the card, so a widened-canvas
 *  card (2160px → 70%) shows its name at the SAME on-screen size as a
 *  founding card — the extra width is the room long names grow into, toward
 *  frame center. Shared with CoverFrame so both cards stay in lockstep. */
export function resolveCardWidth(card?: SpeakerCardPlacement, pngWidthPx?: number): string {
  const s = card?.scale ?? 1;
  const w = pngWidthPx && pngWidthPx > 0 ? pngWidthPx : SPEAKER_STILL_FOUNDING_WIDTH;
  if (s === 1 && w === SPEAKER_STILL_FOUNDING_WIDTH) return CARD_WIDTH;
  return `${(LEGACY_SPEAKER_CARD_WIDTH_PCT * s * w) / SPEAKER_STILL_FOUNDING_WIDTH}%`;
}
/**
 * The LIVE card's box: the same on-screen rectangle the baked PNG would
 * occupy, plus the scale that maps the 2160×1350 still canvas onto it.
 *
 * The live card IS the widened still (SPEAKER_STILL_WIDTH), so the width
 * resolves through the very same `resolveCardWidth` the PNG branch uses —
 * a drag-scale, a channel placement and the founding 70% box all land
 * identically whether the card is baked or drawn. `scale` is exactly
 * cardWidthPx / the still canvas, so the box can never letterbox the still.
 *
 * Pure + exported so the box maths is testable without a Remotion timeline.
 */
export function resolveLiveCardBox(
  card?: SpeakerCardPlacement,
  canvasWidth: number = CANVAS_WIDTH,
): { cardWidthPx: number; scale: number } {
  const cardWidthPx = (parseFloat(resolveCardWidth(card, SPEAKER_STILL_WIDTH)) / 100) * canvasWidth;
  return { cardWidthPx, scale: cardWidthPx / SPEAKER_STILL_WIDTH };
}

/** Extra dwell-after-fade frames: the card stays mounted at opacity 0
 *  for half a second past `endFrame` instead of returning null the very
 *  next frame. Without this, the compositor unmounts the layer one
 *  frame after the fade reaches 0, which on some encoders shows up as
 *  a single-frame "snap" at the 5 s boundary — exactly the artifact the
 *  operator flagged. Mounting an opacity-0 div is free; the snap goes
 *  away. */
const POST_FADE_DWELL_FRAMES = 15;

export const SpeakerCards = ({ speakers, card }: Props): React.JSX.Element => {
  return (
    <>
      {speakers.map((s) => (
        <SingleCard key={`${s.slug}-${s.startSec}`} speaker={s} card={card} />
      ))}
    </>
  );
};

const SingleCard = ({ speaker, card }: { speaker: SpeakerOverlay; card?: SpeakerCardPlacement }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const startFrame = Math.round(speaker.startSec * fps);
  const endFrame = startFrame + Math.round(speaker.durationSec * fps);

  // Hide the layer entirely outside the [start, end + dwell] window so
  // the React tree doesn't carry an offscreen <Img> through the whole
  // video. Inside the dwell, opacity stays 0 — the unmount happens
  // gracefully a few frames AFTER the fade has finished.
  if (frame < startFrame || frame > endFrame + POST_FADE_DWELL_FRAMES) return null;

  const fadeIn = interpolate(
    frame,
    [startFrame, startFrame + FADE_FRAMES],
    [0, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
  );
  const fadeOut = interpolate(
    frame,
    [endFrame - FADE_FRAMES, endFrame],
    [1, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
  );
  const opacity = Math.min(fadeIn, fadeOut);

  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      <SpeakerCardBody speaker={speaker} card={card} opacity={opacity} />
    </AbsoluteFill>
  );
};

/**
 * The card itself — deliberately HOOK-FREE (SingleCard owns the frame maths),
 * so the two branches can be asserted by calling this as a plain function.
 *
 * Branch 1 (`live` absent) is every pipeline render: the baked PNG, exactly
 * the element it has always been.
 *
 * Branch 2 (`live` present) is preview-only — Studio and the onboarding
 * wizard, where no still exists on disk yet. It draws the REAL
 * <SpeakerCardStill> inside the PNG's box, scaled from the top-left, so the
 * preview IS the still: change the name font in the inspector and the card
 * changes, instead of showing a PNG frozen at the last render.
 */
export const SpeakerCardBody = ({
  speaker,
  card,
  opacity,
}: {
  speaker: SpeakerOverlay;
  card?: SpeakerCardPlacement;
  opacity: number;
}): React.JSX.Element => {
  const { leftPx, topPx } = resolveCardPx(card);
  const live = speaker.live;

  if (live) {
    const { cardWidthPx, scale } = resolveLiveCardBox(card);
    return (
      <div
        data-studio-el="speaker"
        style={{
          position: "absolute",
          top: topPx,
          left: leftPx,
          width: cardWidthPx,
          height: (cardWidthPx * SPEAKER_STILL_HEIGHT) / SPEAKER_STILL_WIDTH,
          opacity,
        }}
      >
        <div
          style={{
            position: "relative",
            width: SPEAKER_STILL_WIDTH,
            height: SPEAKER_STILL_HEIGHT,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          <SpeakerCardStill
            name={live.name}
            // descriptionWords: 0 = the name-only creator card. The baked
            // path blanks the role line in ensureSpeakerPng; the live path
            // must blank it HERE or the preview would disagree with the
            // render it is previewing.
            description={card?.descriptionWords === 0 ? "" : live.description}
            nameColor={live.nameColor}
            descColor={live.descColor}
            font={live.font}
            nameFont={live.nameFont}
            fontFaces={live.fontFaces}
            direction={live.direction}
            nameFontPx={card?.nameFontPx}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      // Hit-test seam for the Studio canvas overlay; inert in renders.
      data-studio-el="speaker"
      style={{
        position: "absolute",
        top: topPx,
        left: leftPx,
        width: resolveCardWidth(card, speaker.pngWidthPx),
        opacity,
      }}
    >
      <Img
        src={resolveSrc(speaker.pngSrc)}
        style={{ width: "100%", height: "auto", display: "block" }}
      />
    </div>
  );
};
